import {
  dateFormate,
  dateTimeFormat,
  dateTimeSecondFormat,
  dateDayFormat,
} from 'constants/teacherRecruitment';
import {
  format,
  getTime,
  formatDistanceToNow,
  getMonth,
  getYear,
  getDate,
  intervalToDuration,
  startOfYear,
  endOfYear,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  set,
  parseISO,
  differenceInMinutes,
} from 'date-fns';
import zhTW from 'date-fns/locale/zh-TW';
import vi from 'date-fns/locale/vi';
import enUS from 'date-fns/locale/en-US';
import { t } from 'utils/i18n';
import i18n from 'i18next';

export function fDate(date: Date | string | number) {
  return format(new Date(date), 'dd MMMM yyyy');
}

export function fDateTime(date: Date | string | number) {
  return format(new Date(date), 'dd MMM yyyy p');
}

export function fToDate(date: Date | string | number) {
  return format(new Date(date), dateFormate);
}

export function fCNDate(date: Date | string | number) {
  return format(new Date(date), t('dateFormat.fCNDate', 'MM月dd日 yyyy'));
}

export function fCNDateDay(date: Date | string | number) {
  return format(new Date(date), dateDayFormat, {
    locale: i18n.language === 'zh' ? zhTW : i18n.language === 'vi' ? vi : enUS,
  });
}

export function fCNDateExcludeYear(date: Date | string | number) {
  return format(new Date(date), t('dateFormat.fCNDateExcludeYear', 'MM月dd日'));
}

export function fCNDateExcludeDay(date: Date | string | number) {
  return format(new Date(date), t('dateFormat.fCNDateExcludeDay', 'MM月 yyyy'));
}

export function fToTime(date: Date | string | number) {
  return format(new Date(date), 'HH:mm');
}

export function fTimestamp(date: Date | string | number) {
  return getTime(new Date(date));
}

export function fYear(date: Date | string | number) {
  return getYear(new Date(date));
}

export function fMonth(date: Date | string | number) {
  return getMonth(new Date(date));
}

export function fDay(date: Date | string | number) {
  return getDate(new Date(date));
}

export function fDateTimeSuffix(date: Date | string | number) {
  return format(new Date(date), 'dd/MM/yyyy hh:mm p');
}

export function fDateTime24H(date: Date | string | number) {
  return format(new Date(date), dateTimeFormat);
}

export function fDateTime24HSeconds(date: Date | string | number) {
  return format(new Date(date), dateTimeSecondFormat);
}

export function fToNow(date: Date | string | number) {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
    locale: i18n.language === 'zh' ? zhTW : i18n.language === 'vi' ? vi : enUS,
  });
}

export function fDuration(duration: number) {
  return intervalToDuration({ start: 0, end: duration * 1000 });
}

export function fCurrentYear(date: Date | number) {
  return [startOfYear(date), endOfYear(date)];
}

export function fCurrentMonth(date: Date | number) {
  return [startOfMonth(date), endOfMonth(date)];
}

export function fCurrentWeek(date: Date | number) {
  return [startOfWeek(date), endOfWeek(date)];
}

export function fCurrentDay(date: Date | number) {
  return [startOfDay(date), endOfDay(date)];
}
export function fClearSeconds(date: Date | number) {
  return set(date, { milliseconds: 0, seconds: 0 });
}

export function formatDateWithChineseUnits(date = new Date()) {
  return format(date, 'yyyy年M月d日', { locale: zhTW });
}

export const formatDateForROC = (inputDate: string): string => {
  const dateObject = new Date(inputDate);

  const year = dateObject.getFullYear() - 1911;
  const month = dateObject.getMonth() + 1;
  const day = dateObject.getDate();

  return `自 ${year} 年 ${month} 月 ${day} 日`;
};

export const formatTimeRange = (startTime: string, endTime: string): string => {
  const start = new Date(startTime);
  const end = new Date(endTime);

  const date = format(start, 'yyyy年M月d日', { locale: zhTW });
  const startTimeStr = format(start, 'HH:mm');
  const endTimeStr = format(end, 'HH:mm');

  return `${date} ${startTimeStr} - ${endTimeStr}`;
};

export const formatTimeRangeWithDuration = (
  startTime: string,
  endTime: string,
): string => {
  const start = parseISO(startTime);
  const end = parseISO(endTime);

  const startTimeStr = format(start, 'HH:mm', { locale: zhTW });
  const endTimeStr = format(end, 'HH:mm', { locale: zhTW });
  const durationMinutes = differenceInMinutes(end, start);

  return `${startTimeStr} - ${endTimeStr} (${durationMinutes}分鐘)`;
};

/**
 * 合併日期與時間，輸出 ISO 字串
 * @param dateStr - 日期字串 (完整 Date 格式)
 * @param timeStr - 時間字串 (完整 Date 格式)
 * @returns ISO 格式字串
 */
export const combineDateTimeToISO = (
  dateStr: string,
  timeStr: string,
): string => {
  if (!dateStr || !timeStr) return '';
  const date = new Date(dateStr);
  const time = new Date(timeStr);

  const combined = set(date, {
    hours: time.getHours(),
    minutes: time.getMinutes(),
    seconds: time.getSeconds(),
  });

  return combined.toISOString();
};

// 後端連續課程排課的 dayOfWeek/startTime 是不含時區資訊的裸字串，業務上固定以 UTC+8（台灣時間）為基準
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const TAIWAN_UTC_OFFSET_MS = 8 * HOUR_MS; // 台灣沒有日光節約時間，時差固定為 UTC+8

/**
 * 將連續課程的 dayOfWeek（0=日~6=六）+ startTime（UTC+8 的 'HH:mm' 裸字串）
 * 轉換成裝置時區的星期幾與開始/結束時間。
 *
 * 以「今天」（換算成台灣日期）作為計算基準，而非固定寫死某個歷史日期，
 * 是為了讓換算套用裝置時區「現在」實際生效的日光節約時間規則
 * （例如美加地區夏令/冬令時差不同，寫死日期會導致換算結果整季錯 1 小時）。
 */
export const getWeeklyCourseDeviceTime = (
  dayOfWeek: number,
  startTime: string,
  durationMinutes = 0,
) => {
  const [hour, minute] = startTime.split(':').map(Number);

  // 把「現在」平移 +8 小時後取 UTC 欄位，等同於直接讀出台灣目前的年月日與星期幾
  const nowShiftedToTaiwanWallClock = new Date(
    Date.now() + TAIWAN_UTC_OFFSET_MS,
  );
  const todayDayOfWeekInTaiwan = nowShiftedToTaiwanWallClock.getUTCDay();
  const todayMidnightInTaiwanMs =
    Date.UTC(
      nowShiftedToTaiwanWallClock.getUTCFullYear(),
      nowShiftedToTaiwanWallClock.getUTCMonth(),
      nowShiftedToTaiwanWallClock.getUTCDate(),
    ) - TAIWAN_UTC_OFFSET_MS;

  const dayOffset = dayOfWeek - todayDayOfWeekInTaiwan;
  const start = new Date(
    todayMidnightInTaiwanMs +
      dayOffset * DAY_MS +
      hour * HOUR_MS +
      minute * MINUTE_MS,
  );
  const end = new Date(start.getTime() + durationMinutes * MINUTE_MS);

  return {
    dayOfWeek: start.getDay(),
    startTime: format(start, 'HH:mm'),
    endTime: format(end, 'HH:mm'),
  };
};
